import { useEffect, useMemo, useState } from 'react'
import Toast from '../components/Toast'
import Button from '../components/ui/Button'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import SearchableCurrencySelect from '../components/ui/SearchableCurrencySelect'
import {
  createCurrencyTransitionRule,
  disableCurrencyTransitionRule,
  getCurrencyTransitionRule,
  listCurrencyTransitionRules,
  updateCurrencyTransitionRule,
} from '../services/currencyTransitionRuleService'
import { formatDate, formatDateTime } from '../utils/date'
import { ui } from '../styles/ui'
import {
  MODES,
  STATUSES,
  EMPTY_FORM,
  mapRuleToForm,
  validateForm,
  buildPayload,
  badgeClass,
} from './CurrencyTransitionRulesAdmin.helpers'
import { useTranslation } from '../contexts/TranslationContext'
import { useCurrencyCatalog } from '../contexts/CurrencyCatalogContext'

export default function CurrencyTransitionRulesAdmin() {
  const { t } = useTranslation()
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState({})
  const [confirmDisable, setConfirmDisable] = useState(null)
  const [toast, setToast] = useState(null)
  const { currencies: supportedCurrencies } = useCurrencyCatalog()

  const formTitle = editingId ? t('Edit Rule', 'Edit Rule') : t('Create Rule', 'Create Rule')

  const sortedRules = useMemo(
    () => [...rules].sort((left, right) => left.priority - right.priority),
    [rules],
  )

  async function loadRules() {
    setLoading(true)
    setError('')
    try {
      const data = await listCurrencyTransitionRules()
      setRules(Array.isArray(data) ? data : [])
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || t('Failed to load currency rules.', 'Failed to load currency rules.'))
      setRules([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRules()
  }, [])

  function onFieldChange(name, value) {
    const normalizedValue = ['fromCurrency', 'toCurrency', 'countryCode'].includes(name)
      ? String(value).toUpperCase()
      : value
    setForm((current) => ({ ...current, [name]: normalizedValue }))
    if (formErrors[name]) {
      setFormErrors((current) => ({ ...current, [name]: '' }))
    }
  }

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormErrors({})
  }

  async function onSubmit(event) {
    event.preventDefault()
    const errors = validateForm(form)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setSaving(true)
    try {
      const payload = buildPayload(form)
      if (editingId) {
        await updateCurrencyTransitionRule(editingId, payload)
      } else {
        await createCurrencyTransitionRule(payload)
      }
      setToast({ id: Date.now(), severity: 'info', type: t('Currency Rules', 'Currency Rules'), message: editingId ? t('Rule updated successfully.', 'Rule updated successfully.') : t('Rule created successfully.', 'Rule created successfully.') })
      resetForm()
      await loadRules()
    } catch (requestError) {
      setToast({ id: Date.now(), severity: 'error', type: t('Currency Rules', 'Currency Rules'), message: requestError?.response?.data?.message || requestError.message || t('Failed to save rule.', 'Failed to save rule.') })
    } finally {
      setSaving(false)
    }
  }

  async function startEdit(rule) {
    setSaving(true)
    try {
      const freshRule = await getCurrencyTransitionRule(rule.id)
      if (!freshRule) throw new Error(t('Rule not found', 'Rule not found'))
      setEditingId(freshRule.id)
      setForm(mapRuleToForm(freshRule))
      setFormErrors({})
    } catch (requestError) {
      setToast({ id: Date.now(), severity: 'error', type: t('Currency Rules', 'Currency Rules'), message: requestError?.response?.data?.message || requestError.message || t('Failed to load rule details.', 'Failed to load rule details.') })
    } finally {
      setSaving(false)
    }
  }

  async function onConfirmDisable() {
    if (!confirmDisable) return
    setSaving(true)
    try {
      await disableCurrencyTransitionRule(confirmDisable.id)
      if (editingId === confirmDisable.id) {
        resetForm()
      }
      setToast({ id: Date.now(), severity: 'info', type: t('Currency Rules', 'Currency Rules'), message: t('currency_rules.rule_disabled', `Rule ${confirmDisable.code} disabled.`) })
      setConfirmDisable(null)
      await loadRules()
    } catch (requestError) {
      setToast({ id: Date.now(), severity: 'error', type: t('Currency Rules', 'Currency Rules'), message: requestError?.response?.data?.message || requestError.message || t('Failed to disable rule.', 'Failed to disable rule.') })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageShell><LoadingState text={t('Loading currency transition rules...', 'Loading currency transition rules...')} /></PageShell>

  if (error) {
    return (
      <PageShell>
        <ErrorState
          text={error}
          action={<Button onClick={loadRules}>{t('Retry', 'Retry')}</Button>}
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      {toast ? <Toast alert={toast} onDismiss={() => setToast(null)} /> : null}

      <PageHeader
        title={t('Currency Transition Rules', 'Currency Transition Rules')}
        subtitle={t('Rules used to correct legacy receipt currencies by receipt date and country.', 'Rules used to correct legacy receipt currencies by receipt date and country.')}
        actions={<Button variant="secondary" onClick={loadRules}>{t('Refresh', 'Refresh')}</Button>}
      />

      <SectionCard>
        <p className={ui.states.warning}>
          {t('Amount conversion is disabled by default. MVP rules correct currency codes only and preserve original detected currency in metadata.', 'Amount conversion is disabled by default. MVP rules correct currency codes only and preserve original detected currency in metadata.')}
        </p>
      </SectionCard>

      <SectionCard title={formTitle}>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={t('Code', 'Code')} htmlFor="ctr-code" error={formErrors.code}><Input id="ctr-code" value={form.code} onChange={(e) => onFieldChange('code', e.target.value)} aria-invalid={Boolean(formErrors.code)} /></FormField>
          <FormField label={t('From', 'From')} htmlFor="ctr-from" error={formErrors.fromCurrency}>
            <SearchableCurrencySelect
              id="ctr-from"
              value={form.fromCurrency}
              options={supportedCurrencies}
              onChange={(value) => onFieldChange('fromCurrency', value)}
              emptyLabel={t('Select currency', 'Select currency')}
              searchPlaceholder={t('Search currency', 'Search currency')}
              searchAriaLabel={t('Search from currency', 'Search from currency')}
              selectClassName={ui.forms.select}
            />
          </FormField>
          <FormField label={t('To', 'To')} htmlFor="ctr-to" error={formErrors.toCurrency}>
            <SearchableCurrencySelect
              id="ctr-to"
              value={form.toCurrency}
              options={supportedCurrencies}
              onChange={(value) => onFieldChange('toCurrency', value)}
              emptyLabel={t('Select currency', 'Select currency')}
              searchPlaceholder={t('Search currency', 'Search currency')}
              searchAriaLabel={t('Search to currency', 'Search to currency')}
              selectClassName={ui.forms.select}
            />
          </FormField>
          <FormField label={t('Country', 'Country')} htmlFor="ctr-country" error={formErrors.countryCode}><Input id="ctr-country" maxLength={2} value={form.countryCode} onChange={(e) => onFieldChange('countryCode', e.target.value)} aria-invalid={Boolean(formErrors.countryCode)} /></FormField>
          <FormField label={t('Country name', 'Country name')} htmlFor="ctr-country-name"><Input id="ctr-country-name" value={form.countryName} onChange={(e) => onFieldChange('countryName', e.target.value)} /></FormField>
          <FormField label={t('Effective from', 'Effective from')} htmlFor="ctr-effective" error={formErrors.effectiveFrom}><Input id="ctr-effective" type="date" value={form.effectiveFrom} onChange={(e) => onFieldChange('effectiveFrom', e.target.value)} aria-invalid={Boolean(formErrors.effectiveFrom)} /></FormField>
          <FormField label={t('Legal tender until', 'Legal tender until')} htmlFor="ctr-legal"><Input id="ctr-legal" type="date" value={form.legalTenderUntil} onChange={(e) => onFieldChange('legalTenderUntil', e.target.value)} /></FormField>
          <FormField label={t('Dual display until', 'Dual display until')} htmlFor="ctr-dual"><Input id="ctr-dual" type="date" value={form.dualDisplayUntil} onChange={(e) => onFieldChange('dualDisplayUntil', e.target.value)} /></FormField>
          <FormField label={t('Rate', 'Rate')} htmlFor="ctr-rate" error={formErrors.fixedConversionRate}><Input id="ctr-rate" type="number" step="0.00001" value={form.fixedConversionRate} onChange={(e) => onFieldChange('fixedConversionRate', e.target.value)} aria-invalid={Boolean(formErrors.fixedConversionRate)} /></FormField>
          <FormField label={t('Mode', 'Mode')} htmlFor="ctr-mode" error={formErrors.mode}><select id="ctr-mode" className={ui.select} value={form.mode} onChange={(e) => onFieldChange('mode', e.target.value)}>{MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></FormField>
          <FormField label={t('Status', 'Status')} htmlFor="ctr-status" error={formErrors.status}><select id="ctr-status" className={ui.select} value={form.status} onChange={(e) => onFieldChange('status', e.target.value)}>{STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}</select></FormField>
          <FormField label={t('Priority', 'Priority')} htmlFor="ctr-priority" error={formErrors.priority}><Input id="ctr-priority" type="number" value={form.priority} onChange={(e) => onFieldChange('priority', e.target.value)} aria-invalid={Boolean(formErrors.priority)} /></FormField>
          <FormField label={t('Source URL', 'Source URL')} htmlFor="ctr-source" error={formErrors.sourceUrl}><Input id="ctr-source" value={form.sourceUrl} onChange={(e) => onFieldChange('sourceUrl', e.target.value)} aria-invalid={Boolean(formErrors.sourceUrl)} /></FormField>
          <FormField label={t('Notes', 'Notes')} htmlFor="ctr-notes" className="md:col-span-2"><Input as="textarea" id="ctr-notes" value={form.notes} onChange={(e) => onFieldChange('notes', e.target.value)} /></FormField>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <Button type="submit" disabled={saving}>{editingId ? t('Save changes', 'Save changes') : t('Create rule', 'Create rule')}</Button>
            {editingId ? <Button type="button" variant="secondary" onClick={resetForm}>{t('Cancel edit', 'Cancel edit')}</Button> : null}
          </div>
        </form>
      </SectionCard>

      <SectionCard title={t('Rules', 'Rules')}>
        {sortedRules.length === 0 ? (
          <EmptyState title={t('No rules', 'No rules')} description={t('Create your first currency transition rule.', 'Create your first currency transition rule.')} />
        ) : (
          <DataTable
            columns={[
              { key: 'code', label: t('Code', 'Code'), sortable: true },
              { key: 'fromCurrency', label: t('From', 'From'), sortable: true },
              { key: 'toCurrency', label: t('To', 'To'), sortable: true },
              { key: 'countryCode', label: t('Country', 'Country'), sortable: true },
              { key: 'effectiveFrom', label: t('Effective from', 'Effective from'), sortable: true, sortAccessor: (rule) => rule.effectiveFrom || '' },
              { key: 'legalTenderUntil', label: t('Legal tender until', 'Legal tender until'), sortable: true, sortAccessor: (rule) => rule.legalTenderUntil || '' },
              { key: 'dualDisplayUntil', label: t('Dual display until', 'Dual display until'), sortable: true, sortAccessor: (rule) => rule.dualDisplayUntil || '' },
              { key: 'fixedConversionRate', label: t('Rate', 'Rate'), sortable: true, sortAccessor: (rule) => Number(rule.fixedConversionRate || 0) },
              { key: 'mode', label: t('Mode', 'Mode'), sortable: true },
              { key: 'status', label: t('Status', 'Status'), sortable: true },
              { key: 'priority', label: t('Priority', 'Priority'), sortable: true, sortAccessor: (rule) => Number(rule.priority || 0) },
              { key: 'updatedAt', label: t('Updated', 'Updated'), sortable: true, sortAccessor: (rule) => rule.updatedAt || '' },
              { key: 'actions', label: t('Actions', 'Actions') },
            ]}
            rows={sortedRules}
            renderRow={(rule) => {
              const isDisabled = rule.status === 'inactive' || rule.mode === 'disabled'
              return (
                <tr key={rule.id} className={ui.tableRow}>
                  <td className={ui.tableCellText}>
                    <div className="flex flex-col">
                      <span>{rule.code}</span>
                      {rule.sourceUrl ? <a href={rule.sourceUrl} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Source</a> : null}
                    </div>
                  </td>
                  <td className={ui.tableCellText}>{rule.fromCurrency}</td>
                  <td className={ui.tableCellText}>{rule.toCurrency}</td>
                  <td className={ui.tableCellText}>{rule.countryCode || '-'}</td>
                  <td className={ui.tableCellText}>{formatDate(rule.effectiveFrom)}</td>
                  <td className={ui.tableCellText}>{rule.legalTenderUntil ? formatDate(rule.legalTenderUntil) : '-'}</td>
                  <td className={ui.tableCellText}>{rule.dualDisplayUntil ? formatDate(rule.dualDisplayUntil) : '-'}</td>
                  <td className={ui.tableCellText}>{rule.fixedConversionRate == null ? '-' : String(rule.fixedConversionRate)}</td>
                  <td className={ui.tableCell}><span className={badgeClass(rule.mode)}>{rule.mode}</span></td>
                  <td className={ui.tableCell}><span className={badgeClass(rule.status)}>{rule.status}</span></td>
                  <td className={ui.tableCellText}>{rule.priority}</td>
                  <td className={ui.tableCellText}>{formatDateTime(rule.updatedAt)}</td>
                  <td className={ui.tableCell}>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => startEdit(rule)} disabled={saving}>{t('Edit', 'Edit')}</Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={saving || isDisabled}
                        onClick={() => setConfirmDisable(rule)}
                      >
                        {t('Disable', 'Disable')}
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            }}
          />
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(confirmDisable)}
        title={t('Disable rule', 'Disable rule')}
        message={confirmDisable ? t('currency_rules.disable_rule_confirm', `Disable rule ${confirmDisable.code}?`) : t('Disable selected rule?', 'Disable selected rule?')}
        onClose={() => setConfirmDisable(null)}
        onConfirm={onConfirmDisable}
        confirmText={t('Disable', 'Disable')}
        confirmVariant="danger"
        loading={saving}
      />
    </PageShell>
  )
}
